/**
 * تطبيق الحسابات's own frame (owner, 2026-10-30): the Aliens mark, the app's name, who is signed in, light / dark and
 * sign out — no sidebar, no topbar, no tabs. Its one screen is إثبات التحويلات (features/finance/PayoutProofsPage).
 * Signing out goes back to the app's own sign-in; someone without the grant is told so here, not sent to the system.
 */
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { LockKey, Moon, SignOut, Sun } from '@phosphor-icons/react'
import { AliensMark, Button, EmptyState, IconButton, PageSkeleton } from '@/design-system'
import { NetworkBanner, UpdatePrompt } from '@/layout/SystemBanners'
import { PushPrompt } from '@/layout/PushPrompt'
import { useAuth } from '@/stores/auth'
import { useUi } from '@/stores/ui'
import { hasPermission } from '@shared/permissions'
import { ACCOUNTS_LOGIN, ACCOUNTS_PERMISSION, ACCOUNTS_TITLE } from './accountsApp'

const PayoutProofsPage = lazy(() => import('@/features/finance/PayoutProofsPage'))

export function RequireAccountsAuth() {
  const status = useAuth((s) => s.status)
  const location = useLocation()
  if (status === 'unknown') return <PageSkeleton />
  if (status === 'anonymous') return <Navigate to={ACCOUNTS_LOGIN} replace state={{ from: location.pathname + location.search }} />
  return <Outlet />
}

function useSignOut() {
  const logout = useAuth((s) => s.logout)
  const qc = useQueryClient()
  const go = useNavigate()
  return async () => {
    await logout()
    qc.clear()
    go(ACCOUNTS_LOGIN, { replace: true })
  }
}

function ThemeButton() {
  const theme = useUi((s) => s.theme)
  const setTheme = useUi((s) => s.setTheme)
  const dark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  return <IconButton label={dark ? 'الوضع الفاتح' : 'الوضع الداكن'} icon={dark ? Sun : Moon} onClick={() => setTheme(dark ? 'light' : 'dark')} />
}

export function AccountsShell() {
  const user = useAuth((s) => s.user)
  const granted = useAuth((s) => s.permissionSet)
  const signOut = useSignOut()
  const allowed = hasPermission(granted, ACCOUNTS_PERMISSION)
  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="sticky top-0 z-30 border-b border-border bg-surface/95 px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] backdrop-blur">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-2 lg:max-w-6xl">
          <AliensMark className="h-7 w-9 shrink-0" />
          <div className="min-w-0 flex-1 leading-tight">
            <b className="block truncate text-base text-fg-strong">{ACCOUNTS_TITLE}</b>
            <span className="block truncate text-xs text-fg-muted">{user?.displayName || user?.username}</span>
          </div>
          <ThemeButton />
          <IconButton label="تسجيل الخروج" icon={SignOut} onClick={() => void signOut()} />
        </div>
      </header>
      <NetworkBanner />
      {/* the new-payout notification reaches the devices that switched it on (owner, 2026-10-30) */}
      {allowed && <PushPrompt />}
      <main className="mx-auto w-full min-w-0 max-w-3xl flex-1 lg:max-w-6xl">
        {allowed ? (
          <Suspense fallback={<PageSkeleton />}><PayoutProofsPage /></Suspense>
        ) : (
          <div className="px-4 py-12">
            <EmptyState
              icon={LockKey}
              title="ليس لديك صلاحية"
              description="هذا التطبيق لموظفي الحسابات فقط. اطلب من المسؤول صلاحية «إثبات التحويلات»، أو سجل الدخول بحساب آخر."
              action={<Button variant="primary" icon={SignOut} onClick={() => void signOut()}>تسجيل الخروج</Button>}
            />
          </div>
        )}
      </main>
      <UpdatePrompt />
    </div>
  )
}
